import React from 'react';
import { AlertCircle } from 'lucide-react';
import { INPUT_TOO_LONG_CODE, type SimulationFailure } from '../../../utils/simulationFailure';
import { SIMULATION_FAILED_TEAM_ALERTED, SIMULATION_FAILED_TITLE } from '../failureCopy';

const NEXT_STEPS = 'You can run it again now, or come back to it later from History.';
const RUN_AGAIN = 'Run again';
const EDIT_AND_RUN_AGAIN = 'Edit and run again';
const BACK_TO_HISTORY = 'Back to History';
const REFERENCE_TIME_FORMAT: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
  timeZoneName: 'short',
};

interface SimulationFailedPanelProps {
  failure: SimulationFailure;
  simulationId: number | string;
  simulationName?: string | null;
  /** Used for the reference time when the failure carries no `failedAt` (legacy sentinels). */
  fallbackTime?: string | null;
  /** Omitted in read-only views, which then show no actions. */
  onRunAgain?: () => void;
  onBackToHistory?: () => void;
}

function whatWentWrong(simulationName?: string | null): string {
  return simulationName
    ? `Something went wrong while running “${simulationName}”.`
    : 'Something went wrong while running this simulation.';
}

function formatReferenceTime(value?: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleString(undefined, REFERENCE_TIME_FORMAT);
}

const SimulationFailedPanel: React.FC<SimulationFailedPanelProps> = ({
  failure,
  simulationId,
  simulationName,
  fallbackTime,
  onRunAgain,
  onBackToHistory,
}) => {
  const isInputTooLong = failure.code === INPUT_TOO_LONG_CODE;
  const hasActions = Boolean(onRunAgain || onBackToHistory);
  const explanation = isInputTooLong
    ? failure.message
    : [
        whatWentWrong(simulationName),
        SIMULATION_FAILED_TEAM_ALERTED,
        hasActions ? NEXT_STEPS : null,
      ]
        .filter(Boolean)
        .join(' ');
  const referenceTime = formatReferenceTime(failure.failedAt ?? fallbackTime);

  return (
    <div className="flex items-center justify-center py-16 px-4">
      <div className="text-center max-w-md">
        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="w-8 h-8 text-red-500" aria-hidden="true" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">{SIMULATION_FAILED_TITLE}</h3>
        <p className="text-gray-600">{explanation}</p>
        {hasActions && (
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            {onRunAgain && (
              <button
                type="button"
                onClick={onRunAgain}
                className="bg-primary hover:bg-primary-hover text-white font-semibold py-2 px-4 rounded"
              >
                {isInputTooLong ? EDIT_AND_RUN_AGAIN : RUN_AGAIN}
              </button>
            )}
            {onBackToHistory && (
              <button
                type="button"
                onClick={onBackToHistory}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-2 px-4 rounded"
              >
                {BACK_TO_HISTORY}
              </button>
            )}
          </div>
        )}
        <p className="mt-5 text-xs text-gray-500">
          Reference: simulation #{simulationId}
          {referenceTime ? ` · ${referenceTime}` : ''}
        </p>
      </div>
    </div>
  );
};

export default SimulationFailedPanel;
